import { richFieldToPlainText, type RichField } from '@/types/rich-text';
import type { EditorScreen } from '@/store/editorSlice';
import { resolvePickerUnitDefault } from './answerCollection';

/** Описание блока для подстановок по типу (WEB-182). */
export interface QuizBlockMetaEntry {
  type: string;
  label?: string;
  unitDefault?: string;
}

/**
 * [id блоков-мультивыборов, варианты по id блока, описание по id блока].
 * Кортеж, а не объект: плеер раскладывает его прямо в свои имена.
 */
export type QuizBlockMeta = readonly [
  Set<string>,
  Record<string, { id: string; label: string }[]>,
  Record<string, QuizBlockMetaEntry>,
];

/**
 * F6: stable blockOptions + multi-choice id set from all screens. Плеер зовёт
 * под useMemo по `screens` (данные квиза загрузились или сменились).
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения.
 */
export function collectQuizBlockMeta(screens: EditorScreen[]): QuizBlockMeta {
  const mcIds = new Set<string>();
  const opts: Record<string, { id: string; label: string }[]> = {};
  const meta: Record<string, QuizBlockMetaEntry> = {};
  for (const screen of screens) {
    for (const block of screen.blocks) {
      // WEB-182: type/label descriptor for format-by-type interpolation.
      meta[block.id] = {
        type: block.type,
        // Rich-поле (WEB-672): в label для logic/interpolate — только plain-текст.
        label: block.content != null ? richFieldToPlainText(block.content as RichField) : undefined,
        // WEB-1301: единица измерения по умолчанию — читает сводка ответов
        // лоадера, когда посетитель не переключал kg/lb (cm/ft) сам
        // (см. pickerUnitValueKey в answerCollection.ts для переключённого
        // случая). У остальных типов — undefined. Ревизия десятки 7: общая
        // формула resolvePickerUnitDefault (включая слой AI-`unitDefault`),
        // а не локальная копия — иначе расходилась с рендерером.
        unitDefault:
          block.type === 'weight_picker' || block.type === 'height_picker'
            ? resolvePickerUnitDefault(block)
            : undefined,
      };
      if (block.options && block.options.length > 0) {
        opts[block.id] = block.options.map((o) => ({ id: o.id, label: o.label }));
        // WEB-1301 (F119): multi_step_question пишет values[id] тем же
        // форматом, что multiple_choice (id вариантов через запятую,
        // PublicBlockRendererExtended.tsx case 'multi_step_question') —
        // без этого id→label резолвился бы для одного варианта, а на двух+
        // сводка искала бы опцию с id "o1,o2" и не находила её.
        if (block.type === 'multiple_choice' || block.type === 'multi_step_question') {
          mcIds.add(block.id);
        }
      }
      // WEB-1301 (F378): image_choice держит варианты в СВОЁМ поле
      // imageChoiceOptions, не в block.options (F4-блок, отдельная форма
      // редактора) — общая ветка выше его не видит, поэтому отдельная.
      if (
        block.type === 'image_choice' &&
        Array.isArray((block as unknown as { imageChoiceOptions?: { id: string; label: string }[] }).imageChoiceOptions) &&
        (block as unknown as { imageChoiceOptions: { id: string; label: string }[] }).imageChoiceOptions.length > 0
      ) {
        const imgOpts = (block as unknown as { imageChoiceOptions: { id: string; label: string }[] }).imageChoiceOptions;
        opts[block.id] = imgOpts.map((o) => ({ id: o.id, label: o.label }));
        // Множественный режим пишет id через запятую, как multiple_choice;
        // одиночный — голый id, не входит в mcIds (как single_choice).
        if ((block as unknown as { imageChoiceMultiple?: boolean }).imageChoiceMultiple) {
          mcIds.add(block.id);
        }
      }
    }
  }
  return [mcIds, opts, meta] as const;
}
